import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';

const TravelBrandGeneratorContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-blue);
  }
`;

const QuizButton = styled.a`
  display: inline-block;
  background-color: var(--accent-blue);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-blue);
  transition: all 0.3s ease;
  margin: 20px auto;
  text-align: center;
  cursor: pointer;
  
  &:hover {
    background-color: transparent;
    color: var(--accent-blue);
  }
`;

const QuizButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 30px 0;
`;

const IntroText = styled.p`
  text-align: center;
  max-width: 800px;
  margin: 0 auto var(--spacing-xl);
  font-size: 1.2rem;
  color: var(--text-light);
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: var(--spacing-xxl);
  padding: ${props => props.background ? '40px 20px' : '0'};
  background-color: ${props => props.background || 'transparent'};
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 30px;
`;

const QuizContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  border: 1px solid #000;
  border-radius: 8px;
  padding: 10px;
`;

const ArchetypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const ArchetypeCard = styled.div`
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  background-color: ${props => props.bgColor || '#fff'};
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const ArchetypeName = styled.h3`
  margin-bottom: 10px;
`;

const ArchetypeDescription = styled.p`
  margin-bottom: ${props => props.hasExamples ? '10px' : '0'};
`;

const BrandExamples = styled.p`
  margin-top: 10px;
  font-size: 0.9em;
  font-style: italic;
`;

const DetailSection = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const ArchetypeDetail = styled.article`
  margin-bottom: ${props => props.isLast ? '0' : '30px'};
  padding: 20px;
  background-color: ${props => props.bgColor || '#fff'};
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const RolesGrid = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const RoleCard = styled.div`
  border: 1px solid #eee;
  padding: 20px;
  background-color: #fff;
  border-radius: 5px;
`;

const RoleTitle = styled.h4`
  margin-bottom: 10px;
`;

const FaqContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FaqItem = styled.div`
  margin-bottom: ${props => props.isLast ? '0' : '20px'};
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const FaqQuestion = styled.h4`
  margin-bottom: 10px;
  color: #007bff;
`;

const TravelBrandGenerator = () => {
  const iframeRef = useRef(null);

  useEffect(() => {
    // Function to load Tally embeds
    const loadTallyEmbeds = () => {
      if (typeof window.Tally !== 'undefined') {
        window.Tally.loadEmbeds();
      } else {
        document.querySelectorAll('iframe[data-tally-src]:not([src])').forEach((e) => {
          e.src = e.dataset.tallySrc;
        });
      }
    };

    // Check if Tally script is already loaded
    if (typeof window.Tally === 'undefined') {
      // Create and load the script
      const script = document.createElement('script');
      script.src = 'https://tally.so/widgets/embed.js';
      script.async = true;
      script.onload = loadTallyEmbeds;
      script.onerror = loadTallyEmbeds;
      document.body.appendChild(script);
    } else {
      // Tally already loaded, just initialize embeds
      loadTallyEmbeds();
    }

    // Cleanup function to remove the script when component unmounts
    return () => {
      const tallyScript = document.querySelector('script[src="https://tally.so/widgets/embed.js"]');
      if (tallyScript && tallyScript.parentNode) {
        tallyScript.parentNode.removeChild(tallyScript);
      }
    };
  }, []);

  return (
    <TravelBrandGeneratorContainer>
      <Helmet>
        <title>Travel Brand Generator | Find Your Travel Brand Archetype</title>
        <meta name="description" content="Discover your travel brand's archetype with our quiz. Learn about the 12 archetypes and use our travel brand generator to define your unique voice and attract the right audience." />
        <meta name="keywords" content="travel brand, travel brand creator, travel brand generator, brand archetype, travel marketing, tourism branding" />
      </Helmet>
      
      <Container>
        <Section background="#f8f8f8">
          <PageTitle>Define Your Voice: What's Your Travel Brand's <span>Archetype</span>?</PageTitle>
          <IntroText>
            Your travel brand isn't created in a vacuum; it's co-created every day through the experiences and perceptions of your guests, partners, and audience. However, that doesn't mean you're just a passenger. By understanding and intentionally shaping your brand's core personality – its archetype – you can take ownership of your voice. This clarity helps you cut through the noise, resonate deeply with the *right* travelers, and build a brand story that endures. Use our Travel Brand Generator quiz below to start defining your unique place in the travel landscape.
          </IntroText>
        </Section>
        
        <Section>
          <SectionTitle>Discover Your Archetype: The 12-Question Travel Brand Generator</SectionTitle>
          <p style={{ textAlign: 'center', marginBottom: '30px' }}>Ready to uncover the core personality driving your brand? Answer these 12 questions honestly based on your brand's mission, values, and communication style.</p>
          
          <QuizContainer id="brand-quiz">
            {/* Tally Form Embed */}
            <iframe 
              ref={iframeRef}
              data-tally-src="https://tally.so/embed/mJgzQd?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1" 
              loading="lazy" 
              width="100%" 
              height="5095" 
              frameBorder="0" 
              marginHeight="0" 
              marginWidth="0" 
              title="Travel Brand Generator">
            </iframe>
          </QuizContainer>
        </Section>
        
        <QuizButtonContainer>
          <QuizButton href="#brand-quiz">✨ Find Your Brand Archetype ✨</QuizButton>
        </QuizButtonContainer>
        
        <Section background="#f8f8f8">
          <SectionTitle>Understanding Brand Archetypes</SectionTitle>
          <IntroText>
            Brand archetypes are universal, symbolic characters that reside in the collective unconscious, first deeply explored by psychologist Carl Jung. In branding, they represent fundamental human motivations, values, and personality traits. Tapping into an archetype allows brands to connect with audiences on an instinctive, emotional level. It provides a blueprint for your brand's voice, visual identity, messaging, and overall experience. In the crowded and emotional world of travel, a clearly defined archetype helps you stand out, build trust, and create memorable connections.
          </IntroText>
        </Section>
        
        <Section>
          <SectionTitle>The 12 Brand Archetypes</SectionTitle>
          <ArchetypeGrid>
            <ArchetypeCard bgColor="#FFF9E6">
              <ArchetypeName>😇 The Innocent</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Optimism, simplicity, happiness, purity. Goal: To be happy. Fear: Doing wrong. Brands promise simplicity and goodness.</ArchetypeDescription>
              <BrandExamples>Examples: Disney Cruises, Center Parcs, Club Med</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#F0E6FF">
              <ArchetypeName>🧠 The Sage</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Wisdom, knowledge, expertise, truth. Goal: To understand the world. Fear: Ignorance, deception. Brands provide expertise and information.</ArchetypeDescription>
              <BrandExamples>Examples: National Geographic Expeditions, Smithsonian Journeys, Context Travel</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#E6F5E6">
              <ArchetypeName>🧭 The Explorer</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Freedom, discovery, adventure, independence. Goal: To experience a fulfilling life. Fear: Conformity, entrapment. Brands promote freedom and self-discovery.</ArchetypeDescription>
              <BrandExamples>Examples: REI Adventures, Intrepid Travel, Patagonia</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#FFE6E6">
              <ArchetypeName>🔥 The Outlaw</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Rebellion, liberation, disruption. Goal: To overturn what isn't working. Fear: Powerlessness. Brands challenge norms and empower change.</ArchetypeDescription>
              <BrandExamples>Examples: Virgin Atlantic, G Adventures, Airbnb</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#E6FCFF">
              <ArchetypeName>✨ The Magician</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Transformation, vision, possibility, wonder. Goal: To make dreams come true. Fear: Unintended negative consequences. Brands promise transformation.</ArchetypeDescription>
              <BrandExamples>Examples: Four Seasons, Disney Parks, Emirates</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#FFF0E0">
              <ArchetypeName>💪 The Hero</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Courage, mastery, determination, resilience. Goal: To prove one's worth through courageous acts. Fear: Weakness, vulnerability. Brands empower consumers to achieve.</ArchetypeDescription>
              <BrandExamples>Examples: GoPro, Spartan Race Travel, Outward Bound</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#FFE6F0">
              <ArchetypeName>💗 The Lover</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Intimacy, connection, passion, appreciation. Goal: To be in relationships with people, work, experiences they love. Fear: Being unwanted, alone. Brands foster connection and desire.</ArchetypeDescription>
              <BrandExamples>Examples: Sandals Resorts, Tahiti Tourism, Victoria's Secret Travel</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#FFFBE0">
              <ArchetypeName>🎭 The Jester</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Fun, humor, enjoyment, lightheartedness. Goal: To live in the moment with full enjoyment. Fear: Boredom. Brands promise a good time.</ArchetypeDescription>
              <BrandExamples>Examples: Carnival Cruise Line, Las Vegas Tourism, Virgin Voyages</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#F5F5F0">
              <ArchetypeName>👥 The Everyman</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Belonging, realism, connection, empathy. Goal: To connect with others and belong. Fear: Standing out, being excluded. Brands are down-to-earth and relatable.</ArchetypeDescription>
              <BrandExamples>Examples: Southwest Airlines, Holiday Inn, TripAdvisor</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#E6FFEF">
              <ArchetypeName>🤗 The Caregiver</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Compassion, service, generosity, protection. Goal: To help and protect others. Fear: Selfishness, ingratitude. Brands provide support and care.</ArchetypeDescription>
              <BrandExamples>Examples: Princess Cruises, Beaches Resorts, MedjetAssist</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#FFF8E0">
              <ArchetypeName>👑 The Ruler</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Control, leadership, power, success, order. Goal: To create a prosperous, successful family or community. Fear: Chaos, being overthrown. Brands promise quality and stability.</ArchetypeDescription>
              <BrandExamples>Examples: Ritz-Carlton, British Airways, Cunard</BrandExamples>
            </ArchetypeCard>
            
            <ArchetypeCard bgColor="#E6F4FF">
              <ArchetypeName>🎨 The Creator</ArchetypeName>
              <ArchetypeDescription hasExamples>Values: Innovation, imagination, self-expression, artistry. Goal: To realize a vision. Fear: Mediocrity. Brands enable creativity and innovation.</ArchetypeDescription>
              <BrandExamples>Examples: Airbnb Experiences, Design Hotels, Urban Adventures</BrandExamples>
            </ArchetypeCard>
          </ArchetypeGrid>
        </Section>
        
        <QuizButtonContainer>
          <QuizButton href="#brand-quiz">🧭 Take The Travel Brand Quiz 🧭</QuizButton>
        </QuizButtonContainer>
        
        <Section background="#f8f8f8">
          <SectionTitle>How Archetypes Shape Travel Brands</SectionTitle>
          <DetailSection>
            <ArchetypeDetail bgColor="#FFF9E6">
              <ArchetypeName>😇 The Innocent in Travel</ArchetypeName>
              <p>Innocent travel brands evoke simplicity, safety, and wholesome enjoyment. Think family-friendly resorts with gentle activities, nostalgic destinations offering simple pleasures (like beach towns or countryside retreats), or tours focusing on relaxation and uncomplicated fun. Their marketing uses bright colors, happy faces, and promises of easy, stress-free escapes. They appeal to those seeking comfort, predictability, and a return to simpler times.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#F0E6FF">
              <ArchetypeName>🧠 The Sage in Travel</ArchetypeName>
              <p>Sage travel brands position themselves as experts and guides to deeper understanding. Educational tours, cultural institutions (museums, historical sites), specialized travel agencies focusing on learning (like historical or archaeological tours), and destinations known for their rich history or scientific significance fit here. Their marketing emphasizes knowledge, insight, and thoughtful exploration. They attract travelers who seek to learn, grow, and understand the world on a deeper level.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#E6F5E6">
              <ArchetypeName>🧭 The Explorer in Travel</ArchetypeName>
              <p>The quintessential travel archetype. Explorer brands champion adventure, discovery, and pushing boundaries. Adventure tour operators (hiking, climbing, diving), off-the-beaten-path destinations, budget airlines enabling freedom, and brands selling rugged gear embody this. Marketing focuses on breathtaking landscapes, challenge, freedom, and authenticity. They appeal to independent spirits eager to explore the unknown and find themselves through new experiences.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#FFE6E6">
              <ArchetypeName>🔥 The Outlaw in Travel</ArchetypeName>
              <p>Outlaw travel brands challenge conventions and offer experiences outside the mainstream. Think counter-culture destinations, gritty urban explorations, tours focusing on unconventional activities (e.g., exploring subcultures, abandoned places), or brands that explicitly reject "touristy" experiences. Marketing is often edgy, provocative, and speaks to a desire for liberation from norms. They attract travelers seeking thrills, authenticity on their own terms, and a rejection of the status quo.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#E6FCFF">
              <ArchetypeName>✨ The Magician in Travel</ArchetypeName>
              <p>Magician brands promise transformative, enchanting, and seemingly impossible experiences. Luxury resorts offering unparalleled service and bespoke moments, high-tech travel solutions simplifying complex journeys, wellness retreats focused on profound change, and destinations associated with myth or wonder fit this archetype. Marketing evokes awe, possibility, and dreams-come-true. They attract travelers seeking unique, perspective-shifting experiences and a touch of magic.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#FFF0E0">
              <ArchetypeName>💪 The Hero in Travel</ArchetypeName>
              <p>Hero travel brands empower travelers to overcome challenges and achieve personal feats. Think challenging expeditions (summiting mountains, long-distance treks), voluntourism projects making a tangible impact, sports-focused travel (marathons, training camps), or destinations marketed around overcoming adversity. Marketing highlights courage, mastery, achievement, and making a difference. They attract travelers motivated by challenge, self-improvement, and a desire to test their limits.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#FFE6F0">
              <ArchetypeName>💗 The Lover in Travel</ArchetypeName>
              <p>Lover brands focus on connection, intimacy, beauty, and sensory pleasure. Romantic getaways, luxury spas, boutique hotels with exquisite design, destinations famed for their beauty (Paris, Venice), honeymoon specialists, and fine dining experiences fall here. Marketing is sensual, elegant, and emphasizes shared moments and indulgence. They attract couples, aesthetes, and anyone seeking connection and appreciation of beauty.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#FFFBE0">
              <ArchetypeName>🎭 The Jester in Travel</ArchetypeName>
              <p>Jester brands promise fun, spontaneity, and lighthearted enjoyment. Party destinations, theme parks, quirky accommodations, social tour operators focused on group fun, and budget-friendly travel enabling playful escapes embody this. Marketing is humorous, energetic, and focuses on living in the moment. They attract travelers seeking entertainment, social connection, and a break from seriousness.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#F5F5F0">
              <ArchetypeName>👥 The Everyman in Travel</ArchetypeName>
              <p>Everyman brands offer accessible, relatable, and comfortable travel experiences. Mid-range hotel chains, package holidays, familiar destinations, practical travel apps, and brands emphasizing value and inclusivity fit this archetype. Marketing is friendly, down-to-earth, and reassures travelers that they belong. They appeal to those seeking reliable, unpretentious travel without fuss or exclusivity.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#E6FFEF">
              <ArchetypeName>🤗 The Caregiver in Travel</ArchetypeName>
              <p>Caregiver brands prioritize safety, comfort, service, and nurturing. All-inclusive resorts focused on guest well-being, travel insurance providers, airlines known for attentive service, family-focused tour operators, and accessible travel specialists embody this. Marketing emphasizes trust, security, support, and compassion. They attract travelers seeking reassurance, help, and a supportive travel environment.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#FFF8E0">
              <ArchetypeName>👑 The Ruler in Travel</ArchetypeName>
              <p>Ruler brands represent luxury, exclusivity, control, and high status. Five-star hotels, first-class air travel, exclusive membership clubs, luxury travel agencies, and prestigious destinations project this image. Marketing emphasizes quality, leadership, prestige, and order. They attract affluent travelers seeking the best, recognition, and a sense of belonging to an elite group.</p>
            </ArchetypeDetail>
            
            <ArchetypeDetail bgColor="#E6F4FF" isLast>
              <ArchetypeName>🎨 The Creator in Travel</ArchetypeName>
              <p>Creator brands enable self-expression, customization, and unique experiences crafted by the traveler or the brand. Platforms for booking bespoke itineraries, artistic retreats (photography tours, writing workshops), destinations known for their vibrant arts scene, and travel companies offering highly customizable trips fit here. Marketing highlights innovation, artistry, and the ability to shape your own journey. They attract travelers seeking unique expression, creativity, and participation.</p>
            </ArchetypeDetail>
          </DetailSection>
        </Section>
        
        <Section>
          <SectionTitle>Archetypes Across Different Travel Roles</SectionTitle>
          <p style={{ maxWidth: '800px', margin: '0 auto 30px auto', textAlign: 'center', lineHeight: '1.6' }}>
            While a brand often has one dominant archetype, its expression can be nuanced depending on the specific role within the travel ecosystem. Here's how different players might lean into various archetypes:
          </p>
          
          <RolesGrid>
            <RoleCard>
              <RoleTitle>🏙️ Destinations (TMCs/DMOs)</RoleTitle>
              <p>Can embody almost any archetype depending on their core offering. A historic city might be a Sage, a remote national park an Explorer, a luxury island a Ruler or Lover, a vibrant metropolis a Jester or Creator. Their role is to define the overarching promise of the place.</p>
            </RoleCard>
            
            <RoleCard>
              <RoleTitle>🧩 DMCs (Destination Management Companies)</RoleTitle>
              <p>Often lean towards Caregiver (ensuring smooth logistics), Sage (providing local expertise), or Magician (crafting unique, seamless experiences). Their value lies in expertise and reliable execution.</p>
            </RoleCard>
            
            <RoleCard>
              <RoleTitle>🚌 Tour Operators</RoleTitle>
              <p>Frequently Explorer (adventure tours), Sage (educational/cultural tours), Jester (fun group tours), Hero (challenging expeditions), or Creator (special interest/niche tours). Their archetype reflects the *type* of experience they curate.</p>
            </RoleCard>
            
            <RoleCard>
              <RoleTitle>🏨 Hotels & Accommodation</RoleTitle>
              <p>Commonly Caregiver (hospitality focus), Ruler (luxury), Lover (boutique/romance), Innocent (simple/clean), Everyman (accessible chains), or Creator (unique design/experience hotels). The archetype shapes the atmosphere and service style.</p>
            </RoleCard>
            
            <RoleCard>
              <RoleTitle>🧑‍🏫 Guides</RoleTitle>
              <p>Often Sage (sharing knowledge), Explorer (leading adventures), Jester (entertaining), or Caregiver (ensuring safety and comfort). Their personality directly influences the guest experience.</p>
            </RoleCard>
            
            <RoleCard>
              <RoleTitle>📱 Influencers & Content Creators</RoleTitle>
              <p>Can embody various archetypes based on their niche and persona: Explorer (adventure travel), Lover (luxury/romantic travel), Jester (humorous travel), Hero (overcoming travel challenges), Creator (unique perspectives/artistic content), or Sage (informative travel tips).</p>
            </RoleCard>
          </RolesGrid>
        </Section>
        
        <QuizButtonContainer>
          <QuizButton href="#brand-quiz">🚀 Identify Your Brand Personality 🌍</QuizButton>
        </QuizButtonContainer>
        
        <Section background="#f8f8f8">
          <SectionTitle>Frequently Asked Questions</SectionTitle>
          <FaqContainer>
            <FaqItem>
              <FaqQuestion>Why are brand archetypes important for my travel business?</FaqQuestion>
              <p>Brand archetypes provide a strategic framework for creating a cohesive, emotionally resonant brand identity. In the travel industry—where experiences are highly personal and emotional—archetypes help you connect with travelers on a deeper level. They guide your voice, visuals, service approach, and marketing strategy, ensuring consistency across all touchpoints. This consistency builds trust and recognition, differentiates you from competitors, and attracts ideal clients who resonate with your brand's personality.</p>
            </FaqItem>
            
            <FaqItem>
              <FaqQuestion>How do I determine my travel brand's primary archetype?</FaqQuestion>
              <p>Beyond using our quiz, you can identify your primary archetype by examining: your founding story and core values; your target audience's aspirations and needs; the emotional benefit you provide to travelers; your communication style; your competitors' positioning; and feedback from current customers about how they perceive your brand. Look for patterns in these areas and identify which archetype naturally aligns with most aspects of your brand. The most authentic archetype often emerges from your "why"—the purpose behind your travel business.</p>
            </FaqItem>
            
            <FaqItem>
              <FaqQuestion>Can my travel brand have more than one archetype?</FaqQuestion>
              <p>While it's best to have one dominant archetype that forms your brand's core identity, most successful travel brands incorporate aspects of one or two secondary archetypes that complement or balance the primary one. For example, an Explorer travel brand might have elements of the Sage (providing knowledge) or the Jester (keeping adventures fun). The key is ensuring your primary archetype remains dominant (about 70% of your brand expression) while secondary archetypes add dimension without causing confusion or inconsistency.</p>
            </FaqItem>
            
            <FaqItem>
              <FaqQuestion>How do I implement my brand archetype in my marketing?</FaqQuestion>
              <p>Implementation happens across all aspects of your marketing: Visual identity (colors, fonts, imagery that reflect the archetype's spirit); Voice and messaging (language, tone, storytelling approaches); Content themes (focusing on topics that resonate with your archetype's values); Customer experience (service style, interaction patterns); Channel selection (choosing platforms where your archetype naturally thrives); and Campaigns (creating promotions that align with your archetype's core motivation). The key is consistency—let your archetype guide decisions across all touchpoints.</p>
            </FaqItem>
            
            <FaqItem>
              <FaqQuestion>Should I ever change my travel brand's archetype?</FaqQuestion>
              <p>While archetypes should generally remain stable for brand recognition, there are situations that might warrant evolution: significant business pivot (e.g., shifting from adventure tours to luxury experiences); merger/acquisition; addressing brand perception problems; responding to major market shifts; or aligning with new leadership vision. However, change should be approached carefully, as sudden shifts can confuse customers. Gradual evolution, perhaps leaning more into a previously secondary archetype, is often more effective than a complete change.</p>
            </FaqItem>
            
            <FaqItem>
              <FaqQuestion>How do archetypes affect how travelers perceive and interact with my brand?</FaqQuestion>
              <p>Archetypes tap into universal patterns that travelers intuitively recognize, even subconsciously. Your archetype shapes expectations about your brand experience—travelers naturally anticipate an Explorer brand to offer adventure and discovery, while expecting comfort and care from a Caregiver brand. When your actual experience aligns with these expectations, it creates satisfaction and trust. Additionally, travelers who identify with or aspire to the values of your archetype are more likely to develop emotional connections and loyalty to your brand.</p>
            </FaqItem>
            
            <FaqItem>
              <FaqQuestion>Do travel destinations need brand archetypes too?</FaqQuestion>
              <p>Absolutely. Destinations benefit tremendously from archetypal branding. Consider how Paris embodies the Lover archetype, New Zealand the Explorer, Japan the Sage, and Las Vegas the Jester. Clear archetypal positioning helps destinations differentiate in a crowded market, attract their ideal visitors, shape expectations, guide tourism development decisions, and align stakeholders around a cohesive vision. DMOs and tourism boards can use archetypes to create a unified identity that brings together diverse attractions and experiences under one compelling brand story.</p>
            </FaqItem>
            
            <FaqItem isLast>
              <FaqQuestion>How do I balance authenticity with aspirational positioning in my brand archetype?</FaqQuestion>
              <p>The most effective travel brands find the sweet spot between who they authentically are and who their customers aspire to connect with. Start with authenticity—identify archetypes that genuinely reflect your values, culture, and offerings. Then consider aspirational elements that resonate with your target audience. The key is ensuring you can consistently deliver on your archetypal promise. It's better to authentically embody a less obvious archetype than to inauthentically adopt one that seems more marketable. Travelers quickly detect disconnects between brand messaging and real experience.</p>
            </FaqItem>
          </FaqContainer>
        </Section>
        
        <QuizButtonContainer>
          <QuizButton href="#brand-quiz">💫 Ready to Find Your Archetype? Start Here 💫</QuizButton>
        </QuizButtonContainer>
      </Container>
    </TravelBrandGeneratorContainer>
  );
};

export default TravelBrandGenerator; 